import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postAuth, putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import type { FundLookupDtoIn, NedarimDecodeEntryDtoIn } from '@/models/in/NedarimDecodeModels';
import type { NedarimDecodeEntryDtoOut } from '@/models/out/NedarimDecodeEntryDtoOut';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import FundPicker from './FundPicker';
import { DECODE_ENTRIES_URL } from './nedarimTransactionsFundAllocation';
import styles from './fundAllocationTabs.module.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  funds: FundLookupDtoIn[];
  ruleToEdit: NedarimDecodeEntryDtoIn | null;
}

const EditOrCreateDecodeRuleDialog = ({ open, onClose, onSuccess, funds, ruleToEdit }: Props) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  const isEditing = !!ruleToEdit;

  const [sourceValue, setSourceValue] = useState(ruleToEdit?.sourceValue ?? '');
  const [fundId, setFundId] = useState<number | null>(ruleToEdit?.fundId ?? null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialFund: FundLookupDtoIn | null = ruleToEdit ? { id: ruleToEdit.fundId, name: ruleToEdit.fundName ?? '' } : null;

  const save = async () => {
    setError(null);
    const source = sourceValue.trim();
    if (!source) {
      setError(t('nedarimTransactionsFundAllocation.tabs.rulesSourceRequired'));
      return;
    }
    if (!fundId) {
      setError(t('nedarimTransactionsFundAllocation.tabs.rulesFundRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const body: NedarimDecodeEntryDtoOut = { sourceValue: source, fundId };
      if (isEditing) {
        await putAuth<unknown, object, NedarimDecodeEntryDtoOut>(`${DECODE_ENTRIES_URL}/${ruleToEdit!.id}`, headers, {}, body);
      } else {
        await postAuth<unknown, NedarimDecodeEntryDtoOut>(DECODE_ENTRIES_URL, headers, body);
      }
      onSuccess();
      onClose();
    } catch (err) {
      const serverMessage = (err as { response?: { data?: unknown } })?.response?.data;
      setError(typeof serverMessage === 'string' && serverMessage ? serverMessage : t('nedarimTransactionsFundAllocation.tabs.rulesSaveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(value: boolean) => !value && onClose()}
      title={isEditing ? t('nedarimTransactionsFundAllocation.tabs.editRule') : t('nedarimTransactionsFundAllocation.tabs.addRule')}
    >
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="decode-source">
          {t('nedarimTransactionsFundAllocation.tabs.rulesSourceLabel')}
        </label>
        <input
          id="decode-source"
          className={styles.formInput}
          value={sourceValue}
          onChange={(e) => setSourceValue(e.target.value)}
          placeholder={t('nedarimTransactionsFundAllocation.tabs.rulesSourcePlaceholder')}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.formLabel}>{t('nedarimTransactionsFundAllocation.tabs.rulesFundLabel')}</label>
        <FundPicker
          funds={funds}
          initialFund={initialFund}
          confirmLabel={t('nedarimTransactionsFundAllocation.tabs.chooseFund')}
          onConfirm={(fund) => setFundId(fund.id)}
        />
        {fundId && <span className={`body-sm-regular ${styles.count}`}>{t('nedarimTransactionsFundAllocation.tabs.chosenFund', { fundId })}</span>}
      </div>

      {error && <p className={styles.formError}>{error}</p>}

      <div className={styles.dialogFooter}>
        <Button variant="white" onClick={onClose} disabled={isSubmitting}>
          {t('close')}
        </Button>
        <Button variant="primary" onClick={save} disabled={isSubmitting}>
          {isSubmitting ? t('saving') : t('save')}
        </Button>
      </div>
    </MainDialogLayout>
  );
};

export default EditOrCreateDecodeRuleDialog;
